import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    key: 4,
    class: "flex flex-col"
};
const _hoisted_2 = {
    key: 0,
    class: "my-4 border-0 border-t border-(--border-border-base-soft)",
    "aria-hidden": "true"
};
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { WidgetShell } from "@eudonet/opal";
import axios from "axios";
import ModuleState from "@/shared/components/ModuleState.vue";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
import { useOpinkPermissions } from "@/shared/composables/useOpinkPermissions";
import { useConsentMatrix } from "@/modules/consent/composables/useConsentMatrix";
import { labelKeyFor } from "@/modules/consent/composables/useContactConsentsOverview";
import { listedPurposeCodes, purposeKindOf } from "@/modules/consent/public";
import { resolveConsentPurposeLabel } from "@/modules/consent/composables/useConsentPurposeLabel";
import ConsentCoordinateRow from "@/modules/consent/presentation/components/ConsentCoordinateRow.vue";
import ConsentHistoryDrawer from "@/modules/consent/presentation/components/ConsentHistoryDrawer.vue";
import EditConsentsModal from "@/modules/consent/presentation/components/EditConsentsModal.vue";
import { getContactConsents, getConsentHistory, } from "@/modules/contact/infrastructure/adapters/contactConsentService";
import { isConsentRelevantChannel, toConsentChannelType, } from "@/modules/contact/domain/entities/contactConsent";
/** One group per coordinate, purposes overlaid from the matrix (see `listedPurposeCodes`). */
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactConsentsTab',
    props: {
        contactId: {},
        channels: { default: () => [] }
    },
    setup(__props) {
        const props = __props;
        const { t, locale } = useI18n();
        const { toastError } = useAppToast();
        const { canWriteConsent } = useOpinkPermissions();
        const { matrix, purposeCatalogue, load: loadMatrix } = useConsentMatrix();
        const consentChannels = computed(() => props.channels
            .filter(isConsentRelevantChannel)
            .map((channel) => ({
            channelType: channel.type,
            value: channel.value,
            isPrimary: channel.isPrimary,
        })));
        // Manual load (not the generic `useAsyncData`) because a 403 needs its own branch — a neutral
        // empty state, never the error/toast path — while every other failure keeps the error handling.
        const loading = ref(false);
        const loadError = ref(false);
        const accessDenied = ref(false);
        /**
         * The read result, held directly: `applyDeclaration` patches it in place after a save, and the
         * response is freshly deserialized and referenced by nobody else, so there is nothing to clone.
         * A failure leaves the previous list on screen rather than blanking it.
         */
        const addresses = ref([]);
        async function load() {
            loading.value = true;
            loadError.value = false;
            accessDenied.value = false;
            try {
                addresses.value = (await getContactConsents(props.contactId, consentChannels.value)).addresses;
            }
            catch (e) {
                if (axios.isAxiosError(e) && e.response?.status === 403) {
                    accessDenied.value = true;
                    logger.debug("Consent read forbidden for current user (non-OpinkAdmin)", e);
                }
                else {
                    loadError.value = true;
                    logger.error("Failed to load contact consents", e);
                }
            }
            finally {
                loading.value = false;
            }
        }
        async function loadAll() {
            await Promise.all([load(), loadMatrix()]);
        }
        onMounted(loadAll);
        watch(() => props.contactId, loadAll);
        // Purpose label resolution: prefer the tenant's translated catalogue entry, fall back to the
        // static Tolgee key for the built-in defaults (never a raw untranslated code, R29).
        const purposeByCode = computed(() => new Map(purposeCatalogue.value.map((p) => [p.code, p])));
        function purposeLabel(code) {
            const purpose = purposeByCode.value.get(code);
            return purpose
                ? resolveConsentPurposeLabel(purpose, locale.value)
                : t(`consent.matrix.purpose.${code.toLowerCase()}`);
        }
        function purposeKind(code) {
            return purposeKindOf(purposeByCode.value, code);
        }
        // Qualifier ("Email personnel"…) comes from the contact aggregate: the consent read carries no
        // usage. Indexed once so the per-row lookup is not a scan.
        const channelByValue = computed(() => new Map(props.channels.map((channel) => [`${channel.type}|${channel.value}`, channel])));
        const groups = computed(() => {
            const activePurposesByChannel = new Map();
            for (const entry of matrix.entries) {
                if (!entry.isEnabled)
                    continue;
                const list = activePurposesByChannel.get(entry.channelType) ?? [];
                list.push(entry.purpose);
                activePurposesByChannel.set(entry.channelType, list);
            }
            return addresses.value.map((address) => {
                // Matrix entries are keyed on the OPINK ChannelType ("Sms" for a phone address) while the
                // address carries the display channel ("Phone") — map before the lookup or a phone row
                // would match no active purposes and render empty.
                const activePurposes = activePurposesByChannel.get(toConsentChannelType(address.channelType)) ?? [];
                const existingByPurpose = new Map(address.consents.map((consent) => [consent.purpose, consent]));
                const codes = listedPurposeCodes(activePurposes, existingByPurpose.keys(), purposeKind);
                const contactChannel = channelByValue.value.get(`${address.channelType}|${address.value}`);
                return {
                    channelType: address.channelType,
                    value: address.value,
                    // Same key family the widget uses (`…consentsWidget.channel.*`) — the two surfaces must
                    // never label the same address differently. Falls back to the bare channel when the
                    // contact aggregate has no matching entry (the consent read carries no usage of its own).
                    qualifier: t(contactChannel
                        ? labelKeyFor(contactChannel)
                        : `contact.detail.consentsWidget.channel.${address.channelType === "Phone" ? "phone" : "email"}`),
                    isPrimary: address.isPrimary,
                    purposes: codes.map((code) => ({
                        code,
                        label: purposeLabel(code),
                        status: existingByPurpose.get(code)?.status ?? "NotSet",
                        kind: purposeKind(code),
                    })),
                };
            });
        });
        // ── Edit modal ──────────────────────────────────────────────────────────────
        const editOpen = ref(false);
        /**
         * Which coordinate is being edited, held as its IDENTITY rather than as the group object.
         *
         * `groups` is a computed: every recompute (a declaration patching `addresses`) builds fresh group
         * objects, so a captured reference would keep feeding the modal the statuses from before the save.
         * Resolving the group from the key on each render keeps the modal's props live.
         */
        const editingKey = ref(null);
        const groupKey = (group) => `${group.channelType}|${group.value}`;
        const editing = computed(() => groups.value.find((group) => groupKey(group) === editingKey.value) ?? null);
        function openEdit(group) {
            editingKey.value = groupKey(group);
            editOpen.value = true;
        }
        /**
         * Read-your-writes from the declaration response rather than a full reload: the response is
         * authoritative for every purpose it touched, and re-reading would flash the old values.
         */
        function applyDeclaration(result) {
            const target = editing.value;
            if (!target)
                return;
            // Patching `addresses` re-runs `groups`, which the `editing` computed then re-resolves — that is
            // what keeps a still-open modal showing the statuses it just wrote.
            const address = addresses.value.find((a) => a.channelType === target.channelType && a.value === target.value);
            if (!address)
                return;
            for (const entry of result.results) {
                const existing = address.consents.find((c) => c.purpose === entry.purpose);
                if (existing) {
                    existing.status = entry.status;
                }
                else {
                    address.consents.push({ purpose: entry.purpose, status: entry.status });
                }
            }
        }
        // ── History drawer ──────────────────────────────────────────────────────────
        const drawerOpen = ref(false);
        const historyLoading = ref(false);
        const historyError = ref(false);
        const historyRows = ref([]);
        const historyPurpose = ref(null);
        /**
         * Which coordinate the drawer is showing, held as its identity (like `editingKey`) rather than as a
         * captured group object, so a `groups` recompute cannot leave the drawer pointing at a stale row.
         */
        const historyTarget = ref(null);
        /**
         * Filter options: every purpose the drawer could show, localised here because this component owns
         * the tenant catalogue. Sorted by label so the dropdown reads alphabetically in the active locale.
         */
        const historyPurposeOptions = computed(() => purposeCatalogue.value
            .map((purpose) => ({ code: purpose.code, label: purposeLabel(purpose.code) }))
            .sort((a, b) => a.label.localeCompare(b.label, locale.value)));
        async function loadHistory() {
            const target = historyTarget.value;
            if (!target)
                return;
            historyLoading.value = true;
            historyError.value = false;
            try {
                // Newest first, and grouped per modification, as composed by the BFF from OPINK — no client
                // re-sort and no client re-grouping.
                historyRows.value = await getConsentHistory(props.contactId, target.channelType, target.value, historyPurpose.value ?? undefined);
            }
            catch (e) {
                // Rendered inside the drawer rather than only toasted: an unreachable history and an empty one
                // mean opposite things to a DPO, so the drawer must say which one it is and offer a retry.
                historyError.value = true;
                historyRows.value = [];
                logger.error("Failed to load consent history", e);
                toastError("contact.detail.consents.historyError");
            }
            finally {
                historyLoading.value = false;
            }
        }
        function openHistory(group) {
            historyTarget.value = { channelType: group.channelType, value: group.value };
            // Reset per-opening: a filter left over from the previous coordinate would silently hide entries.
            historyPurpose.value = null;
            historyRows.value = [];
            drawerOpen.value = true;
        }
        /**
         * The drawer's single fetch trigger — the target coordinate AND the purpose filter.
         *
         * Refetching on filter change rather than filtering client-side is deliberate: the purpose filter is
         * applied group-preserving by OPINK (a matched declaration comes back whole), which a client-side
         * filter over already-fetched entries could not reproduce without re-implementing that rule.
         *
         * One combined watcher, NOT an explicit call in `openHistory` plus a watch on the filter: watchers
         * flush after the synchronous block that sets `drawerOpen`, so reopening on another coordinate while
         * a filter was set fires both (resetting the filter to null is a real change) and fetches twice.
         */
        watch([historyTarget, historyPurpose], () => {
            if (drawerOpen.value)
                void loadHistory();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(WidgetShell), {
                    header: _unref(t)('contact.detail.consents.title'),
                    "data-testid": "contact-consents-tab"
                }, {
                    default: _withCtx(() => [
                        (loading.value)
                            ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                            : (loadError.value)
                                ? (_openBlock(), _createBlock(ModuleState, {
                                    key: 1,
                                    type: "error",
                                    title: _unref(t)('contact.detail.consents.loadError'),
                                    "cta-icon": "ri-refresh-line",
                                    "cta-label": _unref(t)('contact.detail.consents.retry'),
                                    onCta: load
                                }, null, 8 /* PROPS */, ["title", "cta-label"]))
                                : (accessDenied.value)
                                    ? (_openBlock(), _createBlock(ModuleState, {
                                        key: 2,
                                        type: "empty",
                                        "data-testid": "contact-consents-no-access",
                                        title: _unref(t)('contact.detail.consents.noAccess')
                                    }, null, 8 /* PROPS */, ["title"]))
                                    : (groups.value.length === 0)
                                        ? (_openBlock(), _createBlock(ModuleState, {
                                            key: 3,
                                            type: "empty",
                                            title: _unref(t)('contact.detail.consents.empty')
                                        }, null, 8 /* PROPS */, ["title"]))
                                        : (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group, index) => {
                                                return (_openBlock(), _createElementBlock(_Fragment, {
                                                    key: `${group.channelType}-${group.value}`
                                                }, [
                                                    (index > 0)
                                                        ? (_openBlock(), _createElementBlock("hr", _hoisted_2))
                                                        : _createCommentVNode("v-if", true),
                                                    _createVNode(ConsentCoordinateRow, {
                                                        "channel-type": group.channelType,
                                                        value: group.value,
                                                        qualifier: group.qualifier,
                                                        "is-primary": group.isPrimary,
                                                        purposes: group.purposes,
                                                        "can-edit": _unref(canWriteConsent),
                                                        onEdit: ($event) => (openEdit(group)),
                                                        onViewHistory: ($event) => (openHistory(group))
                                                    }, null, 8 /* PROPS */, ["channel-type", "value", "qualifier", "is-primary", "purposes", "can-edit", "onEdit", "onViewHistory"])
                                                ], 64 /* STABLE_FRAGMENT */));
                                            }), 128 /* KEYED_FRAGMENT */))
                                        ]))
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header"]),
                (editing.value)
                    ? (_openBlock(), _createBlock(EditConsentsModal, {
                        key: 0,
                        modelValue: editOpen.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((editOpen).value = $event)),
                        "contact-id": __props.contactId,
                        "channel-type": editing.value.channelType,
                        value: editing.value.value,
                        purposes: editing.value.purposes,
                        onSaved: applyDeclaration
                    }, null, 8 /* PROPS */, ["modelValue", "contact-id", "channel-type", "value", "purposes"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(ConsentHistoryDrawer, {
                    modelValue: drawerOpen.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((drawerOpen).value = $event)),
                    "selected-purpose": historyPurpose.value,
                    "onUpdate:selectedPurpose": _cache[2] || (_cache[2] = ($event) => ((historyPurpose).value = $event)),
                    address: historyTarget.value?.value ?? '',
                    entries: historyRows.value,
                    loading: historyLoading.value,
                    "has-error": historyError.value,
                    "purpose-options": historyPurposeOptions.value,
                    onRetry: loadHistory
                }, null, 8 /* PROPS */, ["modelValue", "selected-purpose", "address", "entries", "loading", "has-error", "purpose-options"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
